import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Stack,
  alpha,
  useTheme,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Download,
  Description,
} from '@mui/icons-material';
import { apiService } from '../services/api.service';
import Layout from '../components/Layout';

const Reports: React.FC = () => {
  const theme = useTheme();
  const [exportType, setExportType] = useState<'users' | 'bookings' | 'transactions' | 'feedbacks'>('users');
  const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('csv');
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string>('');

  const handleExport = async () => {
    setExporting(true);
    setError('');
    try {
      const data = await apiService.exportData(exportType, exportFormat);
      
      // Create blob and download
      const blob = new Blob([data], { type: exportFormat === 'csv' ? 'text/csv' : 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${exportType}_${new Date().toISOString().split('T')[0]}.${exportFormat}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.message || 'Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const exportOptions = [
    { value: 'users', label: 'Users', description: 'Export all user data including students and tutors' },
    { value: 'bookings', label: 'Bookings', description: 'Export all booking records with details' },
    { value: 'transactions', label: 'Transactions', description: 'Export all financial transactions' },
    { value: 'feedbacks', label: 'Feedbacks', description: 'Export all user feedback and reviews' },
  ];

  return (
    <Layout>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <Box sx={{
          mb: 4,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2
        }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
              Reports & Exports
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Export platform data in various formats for analysis and reporting.
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, 1fr)'
            },
            gap: 3
          }}
        >
          {exportOptions.map((option) => (
            <Box key={option.value}>
              <Card
                sx={{
                  borderRadius: 3,
                  cursor: 'pointer',
                  border: exportType === option.value ? '2px solid' : '1px solid',
                  borderColor: exportType === option.value ? 'primary.main' : 'divider',
                  bgcolor: exportType === option.value ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                  },
                }}
                onClick={() => setExportType(option.value as any)}
              >
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    <Description color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {option.label}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {option.description}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>

        <Paper sx={{ p: 4, mt: 4, borderRadius: 4 }}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Export Configuration
              </Typography>
            </Box>
            <FormControl fullWidth>
              <InputLabel>Data Type</InputLabel>
              <Select
                value={exportType}
                onChange={(e) => setExportType(e.target.value as any)}
                label="Data Type"
                sx={{ borderRadius: 2 }}
              >
                {exportOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Export Format</InputLabel>
              <Select
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value as any)}
                label="Export Format"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="csv">CSV (Excel compatible)</MenuItem>
                <MenuItem value="json">JSON (Structured data)</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="large"
              startIcon={exporting ? <CircularProgress size={20} /> : <Download />}
              onClick={handleExport}
              disabled={exporting}
              sx={{ borderRadius: 3, py: 1.5, fontWeight: 700 }}
            >
              {exporting ? 'Exporting...' : `Export ${exportOptions.find(o => o.value === exportType)?.label} as ${exportFormat.toUpperCase()}`}
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Layout>
  );
};

export default Reports;

